<script>
  import { onMount } from "svelte";
  import DotIndicator from "../components/DotIndicator.svelte";
  import Header from "../components/Header.svelte";
  import ProjectCard from "../components/web3_projects/ProjectCard.svelte";
  import { fetchWeb3Projects } from "../components/web3_projects/Web3ProjectsData.js";

  let projects = [];
  let loading = true;
  let error = null;
  let activeIndex = 0;
  let scrollContainer;
  let innerContainer;
  let touchStartX = 0;
  let touchEndX = 0;
  let swipeDirection = 0; // -1 for left, 0 for none, 1 for right

  // Mobile detection
  let isMobile = false;

  if (typeof window !== "undefined") {
    isMobile = window.innerWidth < 768;
    window.addEventListener("resize", () => {
      isMobile = window.innerWidth < 768;
    });
  }

  function updateActiveIndex() {
    if (!scrollContainer || !projects.length) return;

    const scrollPosition = scrollContainer.scrollLeft;
    const containerWidth = scrollContainer.clientWidth;
    const maxScroll = scrollContainer.scrollWidth - containerWidth;

    // Calculate progress as a percentage (0 to 1)
    const progress = scrollPosition / maxScroll;
    // Convert to index based on total number of projects
    activeIndex = Math.min(
      Math.round(progress * (projects.length - 1)),
      projects.length - 1
    );
  }

  function handleDotClick(index) {
    if (!scrollContainer || !innerContainer) return;

    const cardWidth = innerContainer.children[0]?.offsetWidth || 0;
    const spacing = 40; // gap-10 = 40px
    const scrollTo = index * (cardWidth + spacing);

    scrollContainer.scrollTo({
      left: scrollTo,
      behavior: "smooth",
    });
  }

  // Mobile touch handling
  function handleTouchStart(e) {
    touchStartX = e.touches[0].clientX;
    swipeDirection = 0;
  }

  function handleTouchMove(e) {
    touchEndX = e.touches[0].clientX;
    const diff = touchEndX - touchStartX;
    const threshold = 20; // Small threshold to start showing visual feedback

    if (Math.abs(diff) > threshold) {
      swipeDirection = diff > 0 ? -1 : 1; // -1 for prev, 1 for next
    } else {
      swipeDirection = 0;
    }
  }

  function handleTouchEnd() {
    const swipeThreshold = 50; // Minimum distance for swipe detection
    const swipeDistance = touchStartX - touchEndX;

    if (swipeDistance > swipeThreshold && activeIndex < projects.length - 1) {
      // Swiped left - next project
      handleDotClick(activeIndex + 1);
    } else if (swipeDistance < -swipeThreshold && activeIndex > 0) {
      // Swiped right - previous project
      handleDotClick(activeIndex - 1);
    }

    // Reset swipe direction
    swipeDirection = 0;
  }

  onMount(async () => {
    try {
      projects = await fetchWeb3Projects();
    } catch (e) {
      error = "Failed to load projects";
      console.error(e);
    } finally {
      loading = false;
    }
  });
</script>

<section id="web3-projects" class="overflow-hidden flex flex-col">
  <!-- Content -->
  <Header title="Web3" subtitle="Projects" />

  <div class="w-screen">
    {#if loading}
      <div class="flex justify-center items-center min-h-[400px]">
        <div class="relative">
          <div
            class="w-16 h-16 rounded-full border-4 border-purple-500/20 animate-pulse"
          ></div>
          <div
            class="absolute inset-0 rounded-full border-t-4 border-purple-500 animate-spin"
          ></div>
        </div>
      </div>
    {:else if error}
      <div class="text-center py-10">
        <div
          class="inline-block p-6 bg-red-500/10 rounded-2xl backdrop-blur-sm"
        >
          <svg
            class="w-12 h-12 text-red-500 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p class="text-red-500 font-medium">{error}</p>
        </div>
      </div>
    {:else}
      <div class="overflow-hidden">
        <div
          bind:this={scrollContainer}
          on:scroll={updateActiveIndex}
          on:touchstart={handleTouchStart}
          on:touchmove={handleTouchMove}
          on:touchend={handleTouchEnd}
          class="flex overflow-x-auto scroll-smooth no-scrollbar -mx-4 md:-mx-10 lg:-mx-16 pb-4 swipe-container"
          class:swipe-left={swipeDirection === 1}
          class:swipe-right={swipeDirection === -1}
        >
          <div
            bind:this={innerContainer}
            class="flex pl-4 md:pl-10 lg:pl-0 pr-0 gap-4 md:gap-10"
          >
            <!-- Larger spacer for desktop, smaller for mobile -->
            <div class="flex-none w-[30px] md:w-[100px] opacity-0"></div>

            {#each projects as project (project.name)}
              <div
                class="flex-none w-[280px] sm:w-[350px] md:w-[450px] lg:w-[600px]"
              >
                <ProjectCard {project} />
              </div>
            {/each}

            <!-- Spacer after last card - smaller on mobile -->
            <div class="flex-none w-[30px] md:w-[60px] opacity-0"></div>
          </div>
        </div>
      </div>

      <!-- Mobile swipe instruction -->
      <div
        class="md:hidden text-center text-xs text-gray-500 dark:text-gray-400 mt-2 mb-2"
      >
        <p class="swipe-instruction">
          <span>← Swipe to navigate →</span>
        </p>
      </div>

      <DotIndicator
        total={projects.length}
        current={activeIndex}
        onDotClick={handleDotClick}
        className="mt-4 md:mt-8"
      />
    {/if}
  </div>
</section>

<style>
  /* Hide scrollbar but keep functionality */
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }

  /* Mobile swipe feedback */
  @media (max-width: 768px) {
    .swipe-container {
      transition: transform 0.2s ease-out;
      touch-action: pan-y; /* Allow vertical scrolling but capture horizontal swipes */
    }

    .swipe-left {
      transform: translateX(-15px); /* Visual feedback when swiping left */
    }

    .swipe-right {
      transform: translateX(15px); /* Visual feedback when swiping right */
    }

    .swipe-instruction {
      padding: 0.5rem;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 0.7;
      }
      50% {
        opacity: 0.3;
      }
    }
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }
</style>
